import apiClient from './apiClient'
import {
  CLAIM_DRAFT, claimDraft, claimById, claimByUser, claimSubmit, claimByUserAndCase,
  cleanupOrphansByUser,
  aiRetrainByUserCase,
  aiProcessByUserCase, aiProcessAggregateByUserCase, aiResultsByUserCase, aiDetectByUserCase,
  imageByUserCase, imageByUserCaseAndSection, imageByUserCaseSectionAndName,
} from './apiEndpoints'
import { getMessage } from '@/utils/messages'
import { log } from '@/utils/logger'
import type { ApiEnvelope } from '@/types/api'
import type { AiResultsResponse, ApiResponse, ValuationDraft } from '@/types'
import type { ClaimDraftPayload, ClaimGetResponse } from '@/types/claim'

// ── Retrain payload types ─────────────────────────────────────
export interface RetrainField {
  FieldName: string
  OldValue: string | null
  NewValue: string
  BoxValue?: [number, number, number, number] | null
}

export interface RetrainSection {
  SectionCode: string
  ImageName: string
  Fields: RetrainField[]
}

export interface RetrainPayload {
  ClaimId: string
  UserId: string
  CaseId: string
  Sections: RetrainSection[]
}

type ServiceError = { response?: { data: unknown; status?: number }; request?: unknown; message?: string }

export type CodedError = Error & { code: string }

// ── Create a new claim draft ──────────────────────────────────
export async function createClaim(
  payload: ClaimDraftPayload
): Promise<ApiEnvelope<ClaimGetResponse>> {
  log.info('createClaim start')
  try {
    const { data } = await apiClient.post<ApiEnvelope<ClaimGetResponse>>(CLAIM_DRAFT, payload)
    if (data.success) {
      log.info('createClaim success', { claimId: String(data.data?.id ?? '') })
    } else {
      log.warn('createClaim returned non-success', { message: data.message, errorCount: String(data.errors?.length ?? 0) })
    }
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.error('createClaim failed — server error', new Error('API error'), { hasResponse: 'true' })
      return err.response.data as ApiEnvelope<ClaimGetResponse>
    } else if (err.request) {
      log.error('createClaim failed — no response', new Error('Network error'))
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('createClaim failed — unexpected', new Error(err.message ?? 'Unknown'))
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}

// ── Update an existing claim draft ───────────────────────────
export async function updateClaim(
  id: string,
  payload: ClaimDraftPayload
): Promise<ApiEnvelope<ClaimGetResponse>> {
  log.info('updateClaim start', { claimId: id })
  try {
    const { data } = await apiClient.put<ApiEnvelope<ClaimGetResponse>>(claimDraft(id), payload)
    if (data.success) {
      log.info('updateClaim success', { claimId: id })
    } else {
      log.warn('updateClaim returned non-success', { claimId: id, message: data.message })
    }
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.error('updateClaim failed — server error', new Error('API error'), { claimId: id })
      return err.response.data as ApiEnvelope<ClaimGetResponse>
    } else if (err.request) {
      log.error('updateClaim failed — no response', new Error('Network error'), { claimId: id })
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('updateClaim failed — unexpected', new Error(err.message ?? 'Unknown'), { claimId: id })
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}

// ── Fetch a single claim by ID ────────────────────────────────
export async function getClaim(
  id: string
): Promise<ApiEnvelope<ClaimGetResponse>> {
  log.info('getClaim', { claimId: id })
  const { data } = await apiClient.get<ApiEnvelope<ClaimGetResponse>>(claimById(id))
  return data
}

// ── Fetch a claim by user ID ──────────────────────────────────
export async function getClaimByUserId(
  userId: string
): Promise<ApiEnvelope<ClaimGetResponse[]>> {
  log.info('getClaimByUserId', { userId })
  try {
    const { data } = await apiClient.get<ApiEnvelope<ClaimGetResponse[]>>(
      claimByUser(userId)
    )
    log.info('getClaimByUserId success', { userId, count: String(data.data?.length ?? 0) })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    log.error('getClaimByUserId failed', new Error(err.message ?? 'Unknown'), { userId })
    if (err.response) return err.response.data as ApiEnvelope<ClaimGetResponse[]>
    if (err.request) throw new Error(getMessage('api.error.no_response'))
    throw new Error(err.message || getMessage('api.error.unexpected'))
  }
}

// ── Submit a completed valuation ──────────────────────────────
export async function submitValuation(
  id: string
): Promise<ApiResponse<ValuationDraft>> {
  log.info('submitValuation', { claimId: id })
  const { data } = await apiClient.post<ApiResponse<ValuationDraft>>(claimSubmit(id), {})
  log.info('submitValuation success', { claimId: id })
  return data
}

// ── Upload multiple images by section ────────────────────────
export async function processAiImages(
  userId: string,
  caseId: string,
  sections: Record<string, File[]>,
  includeOcr: boolean = false,
  includeBoxValue: boolean = true
): Promise<ApiResponse<unknown>> {
  const sectionNames = Object.keys(sections)
  const totalFiles = Object.values(sections).reduce((n, files) => n + files.length, 0)
  log.info('processAiImages start', { userId, caseId, sectionCount: String(sectionNames.length), totalFiles: String(totalFiles), includeOcr: String(includeOcr) })

  const formData = new FormData()
  Object.entries(sections).forEach(([sectionName, files]) => {
    files.forEach((file) => formData.append(sectionName, file))
  })

  try {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      aiProcessByUserCase(userId, caseId, includeOcr, includeBoxValue),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 }
    )
    log.info('processAiImages success', { userId, caseId, success: String(data.success) })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      // 413 Payload Too Large — throw with tagged code instead of returning falsy response
      if (err.response.status === 413) {
        const payloadErr = new Error(getMessage('api.error.payload_too_large')) as CodedError
        payloadErr.code = 'PAYLOAD_TOO_LARGE'
        log.error('processAiImages failed — payload too large', payloadErr, { userId, caseId })
        throw payloadErr
      }
      // All other response errors: return the response data (may have success:false + error details)
      log.error('processAiImages failed — server error', new Error('API error'), { userId, caseId })
      return err.response.data as ApiResponse<unknown>
    } else if (err.request) {
      // Request sent but no response: distinguish timeout from other network errors
      if ((err as { code?: string }).code === 'ECONNABORTED') {
        const timeoutErr = new Error(getMessage('api.error.timeout')) as CodedError
        timeoutErr.code = 'TIMEOUT'
        log.error('processAiImages failed — request timeout', timeoutErr, { userId, caseId })
        throw timeoutErr
      }
      // Other network errors (DNS, dropped connection, offline, CORS, etc.)
      const networkErr = new Error(getMessage('api.error.no_response')) as CodedError
      networkErr.code = 'NETWORK'
      log.error('processAiImages failed — no response', networkErr, { userId, caseId })
      throw networkErr
    } else {
      log.error('processAiImages failed — unexpected', new Error(err.message ?? 'Unknown'), { userId, caseId })
      const unexpectedErr = new Error(err.message || getMessage('api.error.unexpected')) as CodedError
      unexpectedErr.code = 'UNKNOWN'
      throw unexpectedErr
    }
  }
}

// ── Aggregate AI results from already-stored blob images ─────
export async function aggregateAiImages(
  userId: string,
  caseId: string,
  section: string
): Promise<ApiResponse<unknown>> {
  log.info('aggregateAiImages start', { userId, caseId, section })
  try {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      aiProcessAggregateByUserCase(userId, caseId, section)
    )
    log.info('aggregateAiImages success', { userId, caseId, section })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.error('aggregateAiImages failed — server error', new Error('API error'), { userId, caseId })
      return err.response.data as ApiResponse<unknown>
    } else if (err.request) {
      log.error('aggregateAiImages failed — no response', new Error('Network error'), { userId, caseId })
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('aggregateAiImages failed — unexpected', new Error(err.message ?? 'Unknown'), { userId, caseId })
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}

// ── Fetch a claim by user ID and case ID ─────────────────────
export async function getClaimByUserIdAndCaseId(
  userId: string,
  caseId: string
): Promise<ApiEnvelope<ClaimGetResponse>> {
  log.info('getClaimByUserIdAndCaseId', { userId, caseId })
  try {
    const { data } = await apiClient.get<ApiEnvelope<ClaimGetResponse>>(
      claimByUserAndCase(userId, caseId)
    )
    log.info('getClaimByUserIdAndCaseId success', { userId, caseId, found: String(data.success) })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) return err.response.data as ApiEnvelope<ClaimGetResponse>
    if (err.request) throw new Error(getMessage('api.error.no_response'))
    throw new Error(err.message || getMessage('api.error.unexpected'))
  }
}

// ── Delete a claim by user ID and case ID ────────────────────
export async function deleteClaimByUserIdAndCaseId(
  userId: string,
  caseId: string
): Promise<ApiEnvelope<null>> {
  log.info('deleteClaimByUserIdAndCaseId', { userId, caseId })
  try {
    const { data } = await apiClient.delete<ApiEnvelope<null>>(
      claimByUserAndCase(userId, caseId)
    )
    log.info('deleteClaimByUserIdAndCaseId success', { userId, caseId })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) return err.response.data as ApiEnvelope<null>
    if (err.request) throw new Error(getMessage('api.error.no_response'))
    throw new Error(err.message || getMessage('api.error.unexpected'))
  }
}

// ── Delete orphaned blob folders (unsaved drafts) for a user ──
export async function cleanupOrphanedCases(userId: string): Promise<void> {
  log.info('cleanupOrphanedCases', { userId })
  try {
    await apiClient.post(cleanupOrphansByUser(userId), {})
    log.info('cleanupOrphanedCases success', { userId })
  } catch (error: unknown) {
    const err = error as ServiceError
    log.warn('cleanupOrphanedCases failed (non-fatal)', { userId, message: err.message ?? 'Unknown' })
  }
}

// ── Delete all photos for the given sections from DB/blob ────
export async function deleteClaimPhotosBySection(
  userId: string,
  caseId: string,
  sectionNames: string[]
): Promise<void> {
  log.info('deleteClaimPhotosBySection', { userId, caseId, sectionCount: String(sectionNames.length) })
  try {
    await Promise.all(
      sectionNames.map((sectionName) =>
        apiClient.delete(imageByUserCaseAndSection(userId, caseId, sectionName))
      )
    )
    log.info('deleteClaimPhotosBySection success', { userId, caseId })
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.warn('deleteClaimPhotosBySection — server error (non-fatal)', { userId, caseId })
      return
    } else if (err.request) {
      log.error('deleteClaimPhotosBySection — no response', new Error('Network error'), { userId, caseId })
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('deleteClaimPhotosBySection — unexpected', new Error(err.message ?? 'Unknown'), { userId, caseId })
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}

interface DeleteImageData {
  claim_id: string
  section: string
  image: string
  deleted: { succeeded: boolean }
}

// ── Delete a specific photo from DB/blob by image name ───────
export async function deleteClaimPhotoByName(
  userId: string,
  caseId: string,
  sectionName: string,
  imageName: string
): Promise<ApiEnvelope<DeleteImageData>> {
  log.info('deleteClaimPhotoByName', { userId, caseId, sectionName, imageName })
  try {
    const { data } = await apiClient.delete<ApiEnvelope<DeleteImageData>>(
      imageByUserCaseSectionAndName(userId, caseId, sectionName, imageName)
    )
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.warn('deleteClaimPhotoByName — server error (non-fatal)', { userId, caseId, imageName })
      return err.response.data as ApiEnvelope<DeleteImageData>
    } else if (err.request) {
      log.error('deleteClaimPhotoByName — no response', new Error('Network error'), { userId, caseId, imageName })
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('deleteClaimPhotoByName — unexpected', new Error(err.message ?? 'Unknown'), { userId, caseId, imageName })
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}

// ── Delete all photos for a claim across all sections ────────
export async function deleteAllClaimPhotos(
  userId: string,
  caseId: string
): Promise<ApiEnvelope<{ claim_id: string; deleted: number }>> {
  log.info('deleteAllClaimPhotos', { userId, caseId })
  try {
    const { data } = await apiClient.delete<ApiEnvelope<{ claim_id: string; deleted: number }>>(
      imageByUserCase(userId, caseId)
    )
    log.info('deleteAllClaimPhotos success', { userId, caseId, deleted: String(data.data?.deleted ?? 0) })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.warn('deleteAllClaimPhotos — server error (non-fatal)', { userId, caseId })
      return err.response.data as ApiEnvelope<{ claim_id: string; deleted: number }>
    } else if (err.request) {
      log.error('deleteAllClaimPhotos — no response', new Error('Network error'), { userId, caseId })
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('deleteAllClaimPhotos — unexpected', new Error(err.message ?? 'Unknown'), { userId, caseId })
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}

export interface ClaimImageListData {
  claim_id: string
  images: { name: string; url: string }[]
  section: string
}

// ── Get list of UUID image names for a section ───────────────
export async function getClaimImageList(
  userId: string,
  caseId: string,
  section: string
): Promise<ApiEnvelope<ClaimImageListData>> {
  try {
    const { data } = await apiClient.get<ApiEnvelope<ClaimImageListData>>(
      imageByUserCaseAndSection(userId, caseId, section)
    )
    log.info('getClaimImageList success', { userId, caseId, section, count: String(data.data?.images?.length ?? 0) })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.warn('getClaimImageList — server error', { userId, caseId, section })
      return err.response.data as ApiEnvelope<ClaimImageListData>
    } else if (err.request) {
      log.error('getClaimImageList — no response', new Error('Network error'), { userId, caseId, section })
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('getClaimImageList — unexpected', new Error(err.message ?? 'Unknown'), { userId, caseId, section })
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}

// ── Save AI/OCR metadata for a claim (called on Save Draft) ──
export async function saveAiResults(
  userId: string,
  caseId: string,
  data: AiResultsResponse
): Promise<ApiEnvelope<unknown>> {
  log.info('saveAiResults', { userId, caseId })
  try {
    const { data: res } = await apiClient.post<ApiEnvelope<unknown>>(
      aiResultsByUserCase(userId, caseId),
      data
    )
    log.info('saveAiResults success', { userId, caseId })
    return res
  } catch (error: unknown) {
    const err = error as ServiceError
    log.warn('saveAiResults failed (non-fatal)', { userId, caseId })
    if (err.response) return err.response.data as ApiEnvelope<unknown>
    if (err.request) throw new Error(getMessage('api.error.no_response'))
    throw new Error(err.message || getMessage('api.error.unexpected'))
  }
}

// ── Fetch previously processed AI/OCR results for a claim ────
export async function getAiResults(
  userId: string,
  caseId: string
): Promise<ApiEnvelope<AiResultsResponse>> {
  log.info('getAiResults', { userId, caseId })
  try {
    const { data } = await apiClient.get<ApiEnvelope<AiResultsResponse>>(
      aiResultsByUserCase(userId, caseId)
    )
    log.info('getAiResults success', { userId, caseId, hasData: String(!!data.data) })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    log.error('getAiResults failed', new Error(err.message ?? 'Unknown'), { userId, caseId })
    if (err.response) return err.response.data as ApiEnvelope<AiResultsResponse>
    if (err.request) throw new Error(getMessage('api.error.no_response'))
    throw new Error(err.message || getMessage('api.error.unexpected'))
  }
}

export interface AiDetectField {
  value: unknown
  confidence: number
  source: string
}

export interface AiDetectResult {
  claim_id: string
  section: string
  total_images: number
  results: Array<{ image_name: string; fields: Record<string, AiDetectField> }>
}

// ── Re-detect AI for stored blob images (read-only, no DB writes) ─
export async function detectAiImages(
  userId: string,
  caseId: string,
  section: string,
  imageName?: string
): Promise<ApiEnvelope<AiDetectResult>> {
  log.info('detectAiImages', { userId, caseId, section, hasImageName: String(!!imageName) })
  const params = new URLSearchParams({ section })
  if (imageName) params.set('image_name', imageName)
  const { data } = await apiClient.get<ApiEnvelope<AiDetectResult>>(
    `${aiDetectByUserCase(userId, caseId)}${params}`
  )
  return data
}

// ── Notify AI retrain endpoint of user-corrected field values ─
export async function retrainAI(payload: RetrainPayload): Promise<ApiEnvelope<unknown>> {
  log.info('retrainAI', { claimId: payload.ClaimId, sectionCount: String(payload.Sections.length) })
  try {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(aiRetrainByUserCase(payload.UserId, payload.CaseId), payload)
    log.info('retrainAI success', { claimId: payload.ClaimId })
    return data
  } catch (error: unknown) {
    const err = error as ServiceError
    log.error('retrainAI failed', new Error(err.message ?? 'Unknown'), { claimId: payload.ClaimId })
    if (err.response) return err.response.data as ApiEnvelope<unknown>
    if (err.request) throw new Error(getMessage('api.error.no_response'))
    throw new Error(err.message || getMessage('api.error.unexpected'))
  }
}

interface ClaimImageFileData {
  claim_id: string
  section: string
  image: { name: string; url: string }
}

// ── Get the URL for a specific image file from blob storage ──
export async function getClaimImageFile(
  userId: string,
  caseId: string,
  section: string,
  imageName: string
): Promise<string> {
  try {
    const { data } = await apiClient.get<ApiEnvelope<ClaimImageFileData>>(
      imageByUserCaseSectionAndName(userId, caseId, section, imageName)
    )
    if (!data.success || !data.data?.image?.url) {
      throw new Error('Image URL not found in response')
    }
    return data.data.image.url
  } catch (error: unknown) {
    const err = error as ServiceError
    if (err.response) {
      log.error('getClaimImageFile failed — server error', new Error(`HTTP ${(err.response as { status?: number })?.status ?? 'error'}`), { userId, caseId, section, imageName })
      throw new Error(`HTTP ${(err.response as { status?: number })?.status ?? 'error'}`)
    } else if (err.request) {
      log.error('getClaimImageFile failed — no response', new Error('Network error'), { userId, caseId, section, imageName })
      throw new Error(getMessage('api.error.no_response'))
    } else {
      log.error('getClaimImageFile failed — unexpected', new Error(err.message ?? 'Unknown'), { userId, caseId, section, imageName })
      throw new Error(err.message || getMessage('api.error.unexpected'))
    }
  }
}
